import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Loading from '../../../components/loading/Loading';
import {
  getActiveUsersByOrg,
  getCompassDeviceUseByOrg,
  getEmergenciesByOrg,
} from '../../../repository/AnalyticsDAO';

const Analytics = () => {
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingEmergencies, setLoadingEmergencies] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [activeUsersData, setActiveUsersData] = useState(null);
  const [deviceUseData, setDeviceUseData] = useState(null);
  const [emergencyData, setEmergencyData] = useState(null);

  const [error, setError] = useState('');

  const isLoadingAny =
    loadingActiveUsers || loadingDevices || loadingEmergencies || isFetching;

  const handleFetchData = async () => {
    setIsFetching(true);
    setError('');
    setActiveUsersData(null);
    setDeviceUseData(null);
    setEmergencyData(null);

    try {
      // 1. Active Users
      setLoadingActiveUsers(true);
      try {
        const users = await getActiveUsersByOrg();
        setActiveUsersData(users);
      } catch (e) {
        console.error('Error fetching active users', e);
        setError((prev) =>
          prev
            ? `${prev} | Failed to load active users`
            : 'Failed to load active users',
        );
      } finally {
        setLoadingActiveUsers(false);
      }

      // 2. Compass Device Use
      setLoadingDevices(true);
      try {
        const devices = await getCompassDeviceUseByOrg();
        setDeviceUseData(devices);
      } catch (e) {
        console.error('Error fetching device use', e);
        setError((prev) =>
          prev
            ? `${prev} | Failed to load device data`
            : 'Failed to load device data',
        );
      } finally {
        setLoadingDevices(false);
      }

      // 3. Emergencies
      setLoadingEmergencies(true);
      try {
        const emergencies = await getEmergenciesByOrg();
        setEmergencyData(emergencies);
      } catch (e) {
        console.error('Error fetching emergencies', e);
        setError((prev) =>
          prev
            ? `${prev} | Failed to load emergency data`
            : 'Failed to load emergency data',
        );
      } finally {
        setLoadingEmergencies(false);
      }
    } catch (err) {
      console.error(err);
      setError('An unexpected error occurred.');
    } finally {
      setIsFetching(false);
    }
  };

  const renderActiveUsers = () => {
    if (loadingActiveUsers)
      return (
        <Box p={2}>
          <CircularProgress />
        </Box>
      );
    if (!activeUsersData) return null;

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 400 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Org Code</TableCell>
              <TableCell align="right">7 Days</TableCell>
              <TableCell align="right">30 Days</TableCell>
              <TableCell align="right">180 Days</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeUsersData.map((row, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  {row.org_code}
                </TableCell>
                <TableCell align="right">{row.past_week}</TableCell>
                <TableCell align="right">{row.past_month}</TableCell>
                <TableCell align="right">{row.past_six_months}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDeviceUse = () => {
    if (loadingDevices)
      return (
        <Box p={2}>
          <CircularProgress />
        </Box>
      );
    if (!deviceUseData) return null;

    const { one_month, six_months } = deviceUseData;

    // Helper to group data by Org Code
    // Returns: { [orgCode]: { iOS: number, Android: number, ... } }
    const groupByOrg = (data) => {
      const grouped = {};
      data.forEach((item) => {
        if (!grouped[item.org_code]) {
          grouped[item.org_code] = {};
        }
        // Accumulate counts if multiple entries exist for same device type
        grouped[item.org_code][item.device_type] =
          (grouped[item.org_code][item.device_type] || 0) + item.count;
      });
      return grouped;
    };

    // Helper to sum totals by Device Type
    // Returns: { iOS: number, Android: number, ... }
    const sumByDevice = (data) => {
      const totals = {};
      data.forEach((item) => {
        totals[item.device_type] = (totals[item.device_type] || 0) + item.count;
      });
      return totals;
    };

    const oneMonthGrouped = groupByOrg(one_month);
    const sixMonthsGrouped = groupByOrg(six_months);

    const oneMonthTotals = sumByDevice(one_month);
    const sixMonthsTotals = sumByDevice(six_months);

    // Should extract all unique device types found in dataset to dynamically build columns
    const allDeviceTypes = Array.from(
      new Set([
        ...one_month.map((item) => item.device_type),
        ...six_months.map((item) => item.device_type),
      ]),
    ).sort();

    const renderTotalsTable = (totals, title) => (
      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title} - All Orgs Total
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device Type</TableCell>
                <TableCell align="right">Total Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(totals).map(([deviceType, count]) => (
                <TableRow key={deviceType}>
                  <TableCell>{deviceType}</TableCell>
                  <TableCell align="right">{count}</TableCell>
                </TableRow>
              ))}
              {Object.keys(totals).length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );

    const renderOrgTable = (groupedData, title) => (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {title} - By Organization
        </Typography>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 300, mb: 2 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Org</TableCell>
                {allDeviceTypes.map((type) => (
                  <TableCell key={type} align="right">
                    {type}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedData).map(([orgCode, deviceCounts]) => (
                <TableRow key={orgCode}>
                  <TableCell component="th" scope="row">
                    {orgCode}
                  </TableCell>
                  {allDeviceTypes.map((type) => (
                    <TableCell key={type} align="right">
                      {deviceCounts[type] || 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );

    return (
      <Stack spacing={3}>
        <Box sx={{ px: 1 }}>
          <Typography variant="caption" display="block" color="text.secondary">
            * A worker&apos;s last connected device is used for the compass
            device records.
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            * By organization excludes orgs who have had no users connect with a
            compass.
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Last 30 Days
          </Typography>
          {renderTotalsTable(oneMonthTotals, 'Last 30 Days')}
          {renderOrgTable(oneMonthGrouped, 'Last 30 Days')}
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Last 180 Days
          </Typography>
          {renderTotalsTable(sixMonthsTotals, 'Last 180 Days')}
          {renderOrgTable(sixMonthsGrouped, 'Last 180 Days')}
        </Box>
      </Stack>
    );
  };

  const renderEmergencies = () => {
    if (loadingEmergencies)
      return (
        <Box p={2}>
          <CircularProgress />
        </Box>
      );
    if (!emergencyData) return null;

    const { one_month, six_months } = emergencyData;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Last 30 Days
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 300 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Org</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {one_month.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.org_code}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Last 180 Days
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 300 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Org</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {six_months.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.org_code}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 8 }}>
      <Stack spacing={4} sx={{ mt: 2 }}>
        <Typography variant="h5" gutterBottom>
          Usage Analytics
        </Typography>

        <Paper
          elevation={0}
          sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}
        >
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined">
              These queries process a large amount of data and may take a moment
              to complete.
            </Alert>
            <Box>
              <Button
                variant="contained"
                onClick={handleFetchData}
                disabled={isLoadingAny}
                sx={{ minWidth: 160 }}
              >
                {isLoadingAny ? 'Loading...' : 'Load Analytics'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={3}>
          {/* Active Users Card */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Users By Org
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {renderActiveUsers()}
                {!activeUsersData && !loadingActiveUsers && (
                  <Typography variant="body2" color="text.secondary">
                    No data loaded.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Emergencies Card */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Emergencies
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {renderEmergencies()}
                {!emergencyData && !loadingEmergencies && (
                  <Typography variant="body2" color="text.secondary">
                    No data loaded.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Compass Device Use Card */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Compass Device Use
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {renderDeviceUse()}
                {!deviceUseData && !loadingDevices && (
                  <Typography variant="body2" color="text.secondary">
                    No data loaded.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
};

export default withAuthenticationRequired(Analytics, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
